// Entry: only the stylesheet and this loader. React and the app are separate
// chunks (modulepreloaded by index.html, see vite.config.ts) evaluated after
// the static shell has painted (F2: LCP and TBT on phones).
import "./styles.css";

/**
 * Resolves once the static shell's map preview (index.html) is the page's LCP
 * entry, at most ~400 ms. When React replaced it before the browser had
 * reported it (a paint is reported a frame or two after it is drawn), Chrome
 * dropped it and LCP became React's copy, after the app's first render (F2:
 * 2.8 s simulated instead of ~FCP). Browsers without the LCP API wait for the
 * image's decode and one frame.
 */
function bootPreviewPainted(): Promise<void> {
  const img = document.querySelector<HTMLImageElement>("#boot-preview img");
  if (!img) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let obs: PerformanceObserver | undefined;
    const done = () => {
      obs?.disconnect();
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 400);
    if (PerformanceObserver.supportedEntryTypes?.includes("largest-contentful-paint")) {
      obs = new PerformanceObserver((list) => {
        if (list.getEntries().some((e) => (e as PerformanceEntry & { element?: Element | null }).element === img)) done();
      });
      obs.observe({ type: "largest-contentful-paint", buffered: true });
    } else {
      img.decode().catch(() => undefined).then(() => requestAnimationFrame(() => setTimeout(done, 0)));
    }
  });
}

/** the app's JS did not load (offline before the service worker cached it, a deploy replaced the hashed files) */
function bootFailed() {
  const box = document.createElement("div");
  box.className = "bootfail";
  box.setAttribute("role", "alert");
  const p = document.createElement("p");
  p.textContent = "โหลดแอปไม่สำเร็จ ตรวจการเชื่อมต่ออินเทอร์เน็ตแล้วลองใหม่";
  const b = document.createElement("button");
  b.type = "button";
  b.className = "btn-ghost";
  b.textContent = "โหลดหน้าใหม่";
  b.addEventListener("click", () => location.reload());
  box.append(p, b);
  document.body.append(box);
}

async function start() {
  await bootPreviewPainted();
  // fetched in parallel, evaluated as two separate module graphs (two short
  // tasks instead of one long one); nothing of the app is requested before
  // the first paint, so it does not share the link with the HTML and fonts
  const dom = import("react-dom/client");
  const app = import("./AppRoot");
  const root = (await dom).createRoot(document.getElementById("root")!);
  (await app).mount(root);
}
start().catch(bootFailed);

// PWA: register the Workbox service worker after load (prod builds only).
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  addEventListener("load", () => {
    import("workbox-window").then(({ Workbox }) => {
      const wb = new Workbox("/sw.js", { scope: "/" });
      wb.register().then(async () => {
        // keep cached tiles when the app is installed (12 §6.4)
        if (matchMedia("(display-mode: standalone)").matches) await navigator.storage?.persist?.();
      });
    });
  });
}
